import React, { useState, useEffect, useRef } from "react";
import * as Icons from '../Icons';
import SizeClassCorrespondance from "./SizeClassCorrespondance";
import { hourglass } from 'ldrs';

hourglass.register();

export const RegularSearchBar = ({
  inputValue,
  setInputValue,
  placeholder = "Zoeken...",
  responseItems = [],
  columnHeaders = {},
  size = "regular",
  searchFunction,
  searchDelayTimer = 500,
  resultsLoading,
  onItemSelect,
  disabled = false // Add disabled prop
}) => {
  const [internalValue, setInternalValue] = useState('');
  
  const value = inputValue ?? internalValue;
  const setValue = setInputValue ?? setInternalValue;

  const [isFocused, setIsFocused] = useState(false);
  const searchBarRef = useRef(null);
  const timerRef = useRef(null);
  const prevInputValue = useRef(value);
  const isUpdateCalled = useRef(false);

  const handleOutsideClick = (e) => {
    if (searchBarRef.current && !searchBarRef.current.contains(e.target)) {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    if (isFocused && !disabled) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isFocused, disabled]);

  useEffect(() => {
    if (disabled) return; // Don't trigger search when disabled

    if (value !== prevInputValue.current) {
      clearTimeout(timerRef.current);
      isUpdateCalled.current = false;

      timerRef.current = setTimeout(() => {
        if (!isUpdateCalled.current && searchFunction) {
          searchFunction(value);
          isUpdateCalled.current = true;
        }
      }, searchDelayTimer);

      prevInputValue.current = value;
    }

    return () => clearTimeout(timerRef.current);
  }, [value, searchFunction, disabled]);

  const handleBlur = () => {
    if (disabled) return;
    
    if (!isUpdateCalled.current && searchFunction) {
      searchFunction(value);
      isUpdateCalled.current = true;
    }
    clearTimeout(timerRef.current);
  };

  const handleChange = (e) => {
    if (disabled) return;
    setValue(e.target.value);
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsFocused(true);
  };

  // Handle row click
  const handleRowClick = (item) => {
    if (disabled) return;
    
    if (onItemSelect) {
      onItemSelect(item);
    }
    setIsFocused(false);
  };

  return (
    <div
      className={`RegularSearchbarContainer ${SizeClassCorrespondance[size]} ${disabled ? 'disabled' : ''}`}
      ref={searchBarRef}
      style={{ position: "relative" }}
    >
      <div>
        <img
          src={Icons.MagnifyingGlassDark}
          alt="search"
          className='SearchBarIcon'
        />
        <input
          type="text"
          placeholder={placeholder}
          className={`RegularSearchBar ${disabled ? 'disabled' : ''}`}
          onFocus={handleFocus}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
        />
      </div>

      {value.length > 0 && !disabled && (
        resultsLoading ? (
          <div className={`RegularSearchBarResponseList ${isFocused ? "open d-flex justify-content-center align-items-center py-4" : "closed"}`}>
            <l-hourglass size="40" bg-opacity="0.1" speed="1.5" color="gray"></l-hourglass>
          </div>
        ) : (
          responseItems.length > 0 && (
            <table
              className={`RegularSearchBarResponseList ${isFocused ? "open" : "closed"}`}
            >
              <thead>
                <tr>
                  {Object.entries(columnHeaders).map(([key, label]) => (
                    <th key={key}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {responseItems.map((item, rowIndex) => (
                  <tr 
                    key={rowIndex}
                    onClick={() => handleRowClick(item)}
                    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    className="search-result-row"
                  >
                    {Object.keys(columnHeaders).map((key, keyIndex) => (
                      <td key={keyIndex}>{item[key] || ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )
      )}
    </div>
  );
};

export const BorderedSearchBar = ({
  inputValue,
  setInputValue,
  placeholder = "Zoeken...",
  responseItems = [],
  columnHeaders = {},
  size = "regular",
  searchFunction,
  searchDelayTimer = 500,
  resultsLoading,
  error = false,
  label = null,
  onItemSelect,
  disabled = false // Add disabled prop
}) => {
  const [internalValue, setInternalValue] = useState('');
  
  const value = inputValue ?? internalValue;
  const setValue = setInputValue ?? setInternalValue;

  const [isFocused, setIsFocused] = useState(false);
  const searchBarRef = useRef(null);
  const timerRef = useRef(null);
  const prevInputValue = useRef(value);
  const isUpdateCalled = useRef(false);

  const handleOutsideClick = (e) => {
    if (searchBarRef.current && !searchBarRef.current.contains(e.target)) {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    if (isFocused && !disabled) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isFocused, disabled]);

  useEffect(() => {
    if (disabled) return; // Don't trigger search when disabled

    if (value !== prevInputValue.current) {
      clearTimeout(timerRef.current);
      isUpdateCalled.current = false;

      timerRef.current = setTimeout(() => {
        if (!isUpdateCalled.current && searchFunction) {
          searchFunction(value);
          isUpdateCalled.current = true;
        }
      }, searchDelayTimer);

      prevInputValue.current = value;
    }

    return () => clearTimeout(timerRef.current);
  }, [value, searchFunction, disabled]);

  const handleBlur = () => {
    if (disabled) return;
    
    if (!isUpdateCalled.current && searchFunction) {
      searchFunction(value);
      isUpdateCalled.current = true;
    }
    clearTimeout(timerRef.current);
  };

  const handleChange = (e) => {
    if (disabled) return;
    setValue(e.target.value);
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsFocused(true);
  };

  // Handle row click
  const handleRowClick = (item) => {
    if (disabled) return;
    
    if (onItemSelect) {
      onItemSelect(item);
    }
    setIsFocused(false);
  };

  return (
    <div className={`BorderedSearchbarContainer ${label ? 'with-label' : ''} ${SizeClassCorrespondance[size]}`} ref={searchBarRef} style={{ position: "relative" }}>
      {label && <label>{label}</label>}
      <div>
        <img
          src={Icons.MagnifyingGlassDark}
          alt="search"
          className="SearchBarIcon"
        />
        <input
          type="text"
          placeholder={placeholder}
          className={`BorderedSearchBar ${error && "error"}`}
          onFocus={handleFocus}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
        />
      </div>

      {value.length > 0 && !disabled && (
        resultsLoading ? (
          <div className={`BorderedSearchBarResponseList ${isFocused ? "open d-flex justify-content-center align-items-center py-4" : "closed"}`}>
            <l-hourglass size="40" bg-opacity="0.1" speed="1.5" color="gray"></l-hourglass>
          </div>
        ) : (
          responseItems.length > 0 && (
            <table className={`BorderedSearchBarResponseList ${isFocused && responseItems?.length > 0 ? "open" : "closed"}`}>
              <thead>
                <tr>
                  {Object.entries(columnHeaders).map(([key, label]) => (
                    <th className="LightGreyBorder" key={key}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {responseItems.map((item, rowIndex) => (
                  <tr 
                    key={rowIndex}
                    onClick={() => handleRowClick(item)}
                    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    className="search-result-row"
                  >
                    {Object.keys(columnHeaders).map((key, index) => (
                      <td className="LightGreyBorder" key={index}>{item[key] || ""}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )
      )}
    </div>
  );
};

export const UnderlineSearchBar = ({
  inputValue,
  setInputValue,
  placeholder = "Zoeken...",
  responseItems = [],
  columnHeaders = {},
  size = "regular",
  searchFunction,
  searchDelayTimer = 500,
  resultsLoading,
  error = false,
  label = null,
  onItemSelect,
  disabled = false,
  focused = false
}) => {
  const [internalValue, setInternalValue] = useState('');
  
  const value = inputValue ?? internalValue;
  const setValue = setInputValue ?? setInternalValue;

  const [isFocused, setIsFocused] = useState(false);
  const searchBarRef = useRef(null);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const prevInputValue = useRef(value);
  const isUpdateCalled = useRef(false);

  const handleOutsideClick = (e) => {
    if (searchBarRef.current && !searchBarRef.current.contains(e.target)) {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    if (isFocused && !disabled) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isFocused, disabled]);

  useEffect(() => {
    if (disabled) return;

    if (value !== prevInputValue.current) {
      clearTimeout(timerRef.current);
      isUpdateCalled.current = false;

      timerRef.current = setTimeout(() => {
        if (!isUpdateCalled.current && searchFunction) {
          searchFunction(value);
          isUpdateCalled.current = true;
        }
      }, searchDelayTimer);

      prevInputValue.current = value;
    }

    return () => clearTimeout(timerRef.current);
  }, [value, searchFunction, disabled]);

  useEffect(() => {
    if (focused && !disabled && inputRef.current) {
      inputRef.current.focus();
      setIsFocused(true);
    }
  }, [focused, disabled]);

  const handleBlur = () => {
    if (disabled) return;
    
    if (!isUpdateCalled.current && searchFunction) {
      searchFunction(value);
      isUpdateCalled.current = true;
    }
    clearTimeout(timerRef.current);
  };

  const handleChange = (e) => {
    if (disabled) return;
    setValue(e.target.value);
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsFocused(true);
  };

  const handleRowClick = (item) => {
    if (disabled) return;
    
    if (onItemSelect) {
      onItemSelect(item);
    }
    setIsFocused(false);
  };

  return (
    <div className={`UnderlineSearchbarContainer ${label ? 'with-label' : ''} ${SizeClassCorrespondance[size]}`} ref={searchBarRef} style={{ position: "relative" }}>
      {label && <label>{label}</label>}
      <div className="underline-search-wrapper">
        <img
          src={Icons.MagnifyingGlassDark}
          alt="search"
          className="SearchBarIcon"
        />
        <input
          type="text"
          ref={inputRef}
          placeholder={placeholder}
          className={`UnderlineSearchBar ${error && "error"} ${isFocused ? "focused" : ""}`}
          onFocus={handleFocus}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
        />
      </div>

      {value.length > 0 && !disabled && (
        resultsLoading ? (
          <div className={`UnderlineSearchBarResponseList ${isFocused ? "open d-flex justify-content-center align-items-center py-4" : "closed"}`}>
            <l-hourglass size="40" bg-opacity="0.1" speed="1.5" color="gray"></l-hourglass>
          </div>
        ) : (
          responseItems.length > 0 && (
            <table className={`UnderlineSearchBarResponseList ${isFocused && responseItems?.length > 0 ? "open" : "closed"}`}>
              <thead>
                <tr>
                  {Object.entries(columnHeaders).map(([key, label]) => (
                    <th className="LightGreyBorder" key={key}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {responseItems.map((item, rowIndex) => (
                  <tr 
                    key={rowIndex}
                    onClick={() => handleRowClick(item)}
                    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    className="search-result-row"
                  >
                    {Object.keys(columnHeaders).map((key, index) => (
                      <td className="LightGreyBorder" key={index}>{item[key] || ""}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )
      )}
    </div>
  );
};